/**
 * Feature Flags Configuration
 *
 * Controls feature rollout and A/B testing
 * Flags are read from environment variables
 */

/**
 * Parse boolean from environment variable
 * @param {string} value - Environment variable value
 * @param {boolean} defaultValue - Default if not set
 * @returns {boolean}
 */
const parseBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  return value === 'true' || value === '1';
};

/**
 * Feature flags object
 */
export const featureFlags = {
  /**
   * Use new Settings API instead of old /api/settings endpoint
   * When false: Uses old monolithic API
   * When true: Uses new section/key-based API with transformations
   */
  useNewSettingsAPI: parseBoolean(
    process.env.REACT_APP_USE_NEW_SETTINGS_API,
    false
  ),

  /**
   * Enable Settings menu in sidebar navigation
   * When false: Settings menu is hidden (commented out)
   * When true: Settings menu is visible and accessible
   */
  enableSettingsMenu: parseBoolean(
    process.env.REACT_APP_ENABLE_SETTINGS_MENU,
    false
  ),

  /**
   * Log transformation operations for debugging
   * When true: Logs old<->new format transformations to console
   * When false: Silent operation
   */
  debugSettingsTransform: parseBoolean(
    process.env.REACT_APP_DEBUG_SETTINGS_TRANSFORM,
    false
  ),

  /**
   * Enable detailed Settings API call logging
   * When true: Logs all API calls, responses, errors
   * When false: Only errors are logged
   */
  settingsLogging: parseBoolean(
    process.env.REACT_APP_SETTINGS_LOGGING,
    process.env.NODE_ENV === 'development'
  ),

  /**
   * Enable optimistic updates for settings
   * When true: UI updates immediately, rolls back on error
   * When false: UI updates only after server confirms
   */
  optimisticSettingsUpdates: parseBoolean(
    process.env.REACT_APP_OPTIMISTIC_SETTINGS_UPDATES,
    true
  ),

  /**
   * Enable auto-save for settings (debounced)
   * When true: Settings save automatically after typing stops
   * When false: Manual save required
   */
  autoSaveSettings: parseBoolean(
    process.env.REACT_APP_AUTO_SAVE_SETTINGS,
    false
  ),

  /**
   * Auto-save delay in milliseconds
   */
  autoSaveDelay: parseInt(
    process.env.REACT_APP_AUTO_SAVE_DELAY || '2000',
    10
  ),
};

/**
 * Get all feature flags as object
 * @returns {Object} All feature flags
 */
export const getAllFlags = () => {
  return { ...featureFlags };
};

/**
 * Check if a specific flag is enabled
 * @param {string} flagName - Name of the flag
 * @returns {boolean} True if enabled
 */
export const isFlagEnabled = (flagName) => {
  return featureFlags[flagName] === true;
};

/**
 * Log current feature flag status (for debugging)
 */
export const logFeatureFlags = () => {
  console.group('🚩 Feature Flags');
  console.table(featureFlags);
  console.groupEnd();
};

/**
 * Validate feature flag configuration
 * Warns about potentially problematic combinations
 */
export const validateFlags = () => {
  const warnings = [];

  // Can't enable menu without choosing an API mode
  if (featureFlags.enableSettingsMenu && !featureFlags.useNewSettingsAPI) {
    warnings.push(
      '⚠️  Settings menu is enabled but using OLD API. ' +
      'Consider enabling new API or testing both modes.'
    );
  }

  // Debug flags should not be enabled in production
  if (process.env.NODE_ENV === 'production') {
    if (featureFlags.debugSettingsTransform) {
      warnings.push(
        '⚠️  debugSettingsTransform is enabled in production. ' +
        'This may impact performance.'
      );
    }

    if (featureFlags.settingsLogging) {
      warnings.push(
        '⚠️  settingsLogging is enabled in production. ' +
        'This may expose sensitive data in console.'
      );
    }
  }

  // Auto-save with optimistic updates might be confusing
  if (featureFlags.autoSaveSettings && !featureFlags.optimisticSettingsUpdates) {
    warnings.push(
      '⚠️  Auto-save is enabled but optimistic updates are disabled. ' +
      'Users may experience delays.'
    );
  }

  if (warnings.length > 0) {
    console.group('⚠️  Feature Flag Warnings');
    warnings.forEach(warning => console.warn(warning));
    console.groupEnd();
  }

  return warnings;
};

/**
 * Get environment info
 */
export const getEnvironmentInfo = () => {
  return {
    nodeEnv: process.env.NODE_ENV,
    settingsApiUrl: process.env.REACT_APP_SETTINGS_API_URL,
    mainApiUrl: process.env.REACT_APP_API_URL,
    featureFlags: featureFlags
  };
};

// Run validation on load in development
if (process.env.NODE_ENV === 'development') {
  validateFlags();
}

// Export default for convenience
export default featureFlags;
